import * as React from "react";
import { observer } from "mobx-react";
import { Card, Select, Input, Col, Row, Form } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { qaCategoriesStore } from "../Stores/QACategoriesStore";
import { Button } from "Components/General/Button";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { projectsDataStore } from "Stores";
import { API } from "General/API";
import { AddFiles } from "Components/General/AddFilesInput/AddFiles";
import { PriorityLabel } from "./Priority";
import { API_URL } from "General/APIEndpoints";
import { QuestionLimit } from "./QuestionLimit";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { RouterStore } from "Config/CustomBrowserRouter";
import { QA_LINK } from "../enums";

export interface QANewQuestionInstanceProps {
  refreshQaDetails?: () => void;
  initialData?: API_Question;
  attachments?: API_FileDataEntity[];
  createRelativeQuestion?: boolean;
}

interface QANewQuestionProps extends QANewQuestionInstanceProps, WithTranslation, ModalComponentProps {}

interface QANewQuestionState {
  limits: API_QuestionLimitResponse;
  loading: boolean;
}

export const QANewQuestion = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<QANewQuestionProps, QANewQuestionState> {
      constructor(props: QANewQuestionProps) {
        super(props);
        this.state = {
          limits: {
            highLimit: 0,
            normalLimit: 0,
            period: "ALL",
          },
          loading: false,
        };
      }

      componentDidMount() {
        // when calling this modal from Documents module - categories have to be fetched.
        if (qaCategoriesStore.state === "RAW") {
          qaCategoriesStore.fetch();
        }
        this.fetchLimits();
      }

      fetchLimits = async () => {
        try {
          const limits = await API.getData(API_URL.QA.getQuestionLimit(projectsDataStore.projectId));
          this.setState({ limits });
        } catch (error) {
          throw error;
        }
      };

      handleEdit = async (values: API_Question) => {
        const {
          t,
          initialData: { id },
        } = this.props;
        await API.editData(API_URL.QA.edit(projectsDataStore.projectId, id), values);
        notificationSuccess(t("Success"), t("Question was edited"));
        this.props.hideModal();
        if (this.props.refreshQaDetails) {
          this.props.refreshQaDetails();
        }
      };

      handleSubmit = async (values) => {
        const { t, initialData, createRelativeQuestion } = this.props;
        // this.props.form.validateFieldsAndScroll(async (err, values) => {
        // if (!err) {
        try {
          this.setState({ loading: true });
          if (values.files) {
            values.files = values.files.map((item: API_FileDataEntity) => `@id${item.id}`);
          }

          if (!initialData || createRelativeQuestion) {
            if (createRelativeQuestion) {
              (values as API_Question).parentQuestionId = initialData.parentQuestionId;
            }
            const response = await API.newData<API_Question>(
              API_URL.QA.createNewQuestion(projectsDataStore.projectId),
              values
            );
            notificationSuccess(t("Success"), t("Draft question created"));
            this.props.hideModal();
            RouterStore.push(`/project/${projectsDataStore.projectId}/qa/${QA_LINK.QUESTIONS}/${response.id}`);
          } else {
            this.handleEdit(values);
          }
        } catch (err) {
          throw err;
        } finally {
          this.setState({ loading: false });
        }
      };
      //   });
      // };

      render() {
        const { t, initialData, createRelativeQuestion } = this.props;
        const { limits, loading } = this.state;
        return (
          <>
            <Form
              onFinish={this.handleSubmit}
              className="ant-form-item-grey-label"
              labelCol={{ span: 6 }}
              colon={false}
            >
              <Card variant="outlined" styles={{ body: { padding: "24px 0" } }}>
                <Form.Item
                  label={t("Category")}
                  name="categoryId"
                  initialValue={initialData && initialData.categoryId}
                  rules={[{ required: true, message: t("Please provide a category") }]}
                >
                  <Select
                    disabled={createRelativeQuestion}
                    autoFocus={!createRelativeQuestion}
                    loading={qaCategoriesStore.state === "LOADING"}
                  >
                    {qaCategoriesStore.activeCategories.map((item) => (
                      <Select.Option key={`${item.id}`} value={item.id}>
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label={t("Topic")}
                  name="topic"
                  initialValue={initialData && initialData.topic}
                  rules={[{ required: true, message: t("Please provide a topic") }]}
                >
                  <Input name="topic" placeholder={t("Topic")} />
                </Form.Item>
                <Form.Item
                  label={t("Priority")}
                  name="priority"
                  initialValue={(initialData && initialData.priority) || "NORMAL"}
                >
                  <Select>
                    <Select.Option value="HIGH">
                      <PriorityLabel type={"HIGH" as API_QUESTION_PRIORITY} />
                    </Select.Option>
                    <Select.Option value="NORMAL">
                      <PriorityLabel type={"NORMAL" as API_QUESTION_PRIORITY} />
                    </Select.Option>
                    <Select.Option value="LOW">
                      <PriorityLabel type={"LOW" as API_QUESTION_PRIORITY} />
                    </Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label={t("Files")}
                  name="files"
                  initialValue={(initialData && initialData.attachments) || this.props.attachments}
                >
                  <AddFiles fileUploadType="EXIST" minimumAccessLevel="VIEW_WATERMARKED" />
                </Form.Item>
                <Form.Item
                  label={t("Question")}
                  name="question"
                  labelCol={{ xl: { span: 24 } }}
                  initialValue={(initialData && initialData.question) || ""}
                  rules={[{ required: true, message: t("Please provide a question") }]}
                >
                  <Input.TextArea
                    placeholder={t("Enter your question here")}
                    rows={4}
                    style={{ padding: 10, boxShadow: "0 0px 8px -2px rgba(0, 0, 0, 0.2)" }}
                  />
                </Form.Item>
              </Card>
              <ButtonsRow justify="space-between" align="middle">
                <Col span={16}>
                  <Row align="middle" justify="start">
                    <QuestionLimit priority="HIGH" value={limits.highLimit} />
                    <QuestionLimit priority="NORMAL" value={limits.normalLimit} />
                  </Row>
                </Col>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {t("Save Draft")}
                </Button>
              </ButtonsRow>
            </Form>
          </>
        );
      }
    }
  )
);
